import {
  BarChart3,
  Calendar,
  DollarSign,
  Layers,
  Link,
  MousePointerClick,
  Settings,
  Target,
  TrendingUp,
  User,
} from 'lucide-react'
import { useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import {
  googleAdsListCampaigns,
  type GoogleAdsListCampaignsResult,
} from 'sprites-types/workflow/tools/googleAdsListResources'
import { ObjectDisplay } from '../shared/ObjectDisplay'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

type ResourceItem = GoogleAdsListCampaignsResult[number]
type ResourceResult = GoogleAdsListCampaignsResult | ResourceItem

function getStatusColor(status: string) {
  switch (status) {
    case 'ENABLED':
      return 'bg-green-100 text-green-800'
    case 'PAUSED':
      return 'bg-yellow-100 text-yellow-800'
    case 'REMOVED':
      return 'bg-red-100 text-red-800'
    case 'UNKNOWN':
    case 'UNSPECIFIED':
      return 'bg-gray-100 text-gray-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

function getCampaignTypeColor(type: string) {
  switch (type) {
    case 'SEARCH':
      return 'bg-blue-100 text-blue-800'
    case 'DISPLAY':
      return 'bg-purple-100 text-purple-800'
    case 'VIDEO':
      return 'bg-red-100 text-red-800'
    case 'SHOPPING':
      return 'bg-orange-100 text-orange-800'
    case 'PERFORMANCE_MAX':
      return 'bg-indigo-100 text-indigo-800'
    case 'HOTEL':
      return 'bg-cyan-100 text-cyan-800'
    case 'LOCAL':
      return 'bg-green-100 text-green-800'
    case 'SMART':
      return 'bg-pink-100 text-pink-800'
    case 'DISCOVERY':
      return 'bg-teal-100 text-teal-800'
    case 'MULTI_CHANNEL':
      return 'bg-violet-100 text-violet-800'
    case 'LOCAL_SERVICES':
      return 'bg-emerald-100 text-emerald-800'
    case 'TRAVEL':
      return 'bg-sky-100 text-sky-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

function formatCurrency(amount: number | null) {
  if (amount === null || amount === undefined) return 'N/A'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
}

function formatAdStrength(strength: string | number | null | undefined): string | null {
  if (!strength) return null

  const str = String(strength).toUpperCase()

  // Handle numeric values (Google Ads API sometimes returns numbers)
  if (str === '0' || str === 'NO_ADS') return 'NO_ADS'
  if (str === '1' || str === 'POOR') return 'POOR'
  if (str === '2' || str === 'AVERAGE') return 'AVERAGE'
  if (str === '3' || str === 'GOOD') return 'GOOD'
  if (str === '4' || str === 'EXCELLENT') return 'EXCELLENT'

  // Handle string values
  if (
    [
      'EXCELLENT',
      'GOOD',
      'AVERAGE',
      'POOR',
      'PENDING',
      'NO_ADS',
      'UNKNOWN',
      'UNSPECIFIED',
    ].includes(str)
  ) {
    return str
  }

  return null
}

function getAdStrengthColor(strength: string | number | null | undefined) {
  const formatted = formatAdStrength(strength)
  if (!formatted) return 'bg-gray-100 text-gray-800'

  switch (formatted) {
    case 'EXCELLENT':
      return 'bg-blue-100 text-blue-800'
    case 'GOOD':
      return 'bg-green-100 text-green-800'
    case 'AVERAGE':
      return 'bg-yellow-100 text-yellow-800'
    case 'POOR':
      return 'bg-red-100 text-red-800'
    case 'PENDING':
      return 'bg-gray-100 text-gray-800'
    case 'NO_ADS':
      return 'bg-gray-100 text-gray-800'
    case 'UNKNOWN':
    case 'UNSPECIFIED':
      return 'bg-gray-100 text-gray-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

function CampaignItem({ campaign, className }: { campaign: any; className?: string }) {
  const isEnabled = campaign.status === 'ENABLED'

  return (
    <div
      className={`rounded-3xl border border-gray-200/50 text-black shadow-sm backdrop-blur-sm transition-shadow hover:shadow-md ${isEnabled ? 'bg-white/70' : 'bg-gray-50 opacity-60'} ${className}`}
    >
      <div className="px-5 py-4">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100">
              <TrendingUp className="h-4 w-4 text-blue-600" />
            </div>
            <div
              className={`text-sm font-semibold ${isEnabled ? 'text-gray-900' : 'text-gray-500'}`}
            >
              {campaign.campaignName}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span
              className={`rounded-full px-2.5 py-1 text-xs font-medium ${getCampaignTypeColor(campaign.campaignType)}`}
            >
              {campaign.campaignType}
            </span>
            <span
              className={`rounded-full px-2.5 py-1 text-xs font-medium ${getStatusColor(campaign.status)}`}
            >
              {campaign.status}
            </span>
          </div>
        </div>

        {!isEnabled && (
          <div className="mb-3 text-xs italic text-gray-500">Campaign is not enabled</div>
        )}

        <div className="grid grid-cols-2 gap-4 text-xs">
          <div className="flex items-start gap-2.5">
            <Settings className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
            <div className="min-w-0">
              <div
                className={`truncate font-medium ${isEnabled ? 'text-gray-900' : 'text-gray-600'}`}
              >
                {campaign.campaignId}
              </div>
              <div className={isEnabled ? 'text-gray-500' : 'text-gray-400'}>Campaign ID</div>
            </div>
          </div>

          <div className="flex items-start gap-2.5">
            <User className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
            <div className="min-w-0">
              <div
                className={`truncate font-medium ${isEnabled ? 'text-gray-900' : 'text-gray-600'}`}
              >
                {campaign.customerName || campaign.customerId}
              </div>
              <div className={isEnabled ? 'text-gray-500' : 'text-gray-400'}>Customer</div>
            </div>
          </div>

          {campaign.budget && campaign.budget.amount && (
            <div className="flex items-start gap-2.5">
              <DollarSign className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
              <div className="min-w-0">
                <div
                  className={`truncate font-medium ${isEnabled ? 'text-gray-900' : 'text-gray-600'}`}
                >
                  {formatCurrency(campaign.budget.amount)}
                </div>
                <div className={isEnabled ? 'text-gray-500' : 'text-gray-400'}>Budget</div>
              </div>
            </div>
          )}

          {campaign.biddingStrategy && (
            <div className="flex items-start gap-2.5">
              <Target className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
              <div className="min-w-0">
                <div
                  className={`truncate font-medium ${isEnabled ? 'text-gray-900' : 'text-gray-600'}`}
                >
                  {campaign.biddingStrategy}
                </div>
                <div className={isEnabled ? 'text-gray-500' : 'text-gray-400'}>Bidding</div>
              </div>
            </div>
          )}
        </div>

        {(campaign.startDate || campaign.endDate) && (
          <div className="mt-4 border-t border-gray-200 pt-3">
            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5 text-green-500" />
                {campaign.startDate && (
                  <span className={isEnabled ? 'text-gray-600' : 'text-gray-500'}>
                    Start:{' '}
                    <span
                      className={`font-medium ${isEnabled ? 'text-gray-900' : 'text-gray-600'}`}
                    >
                      {campaign.startDate}
                    </span>
                  </span>
                )}
              </div>
              {campaign.endDate && (
                <span className={isEnabled ? 'text-gray-600' : 'text-gray-500'}>
                  End:{' '}
                  <span className={`font-medium ${isEnabled ? 'text-gray-900' : 'text-gray-600'}`}>
                    {campaign.endDate}
                  </span>
                </span>
              )}
            </div>
          </div>
        )}

        {(campaign.servingStatus || campaign.adServingOptimizationStatus) && (
          <div className="mt-4 border-t border-gray-200 pt-3">
            <div className="space-y-2 text-xs">
              {campaign.servingStatus && (
                <div className="flex items-center gap-1.5">
                  <BarChart3 className="h-3.5 w-3.5 text-purple-500" />
                  <span className={`font-medium ${isEnabled ? 'text-gray-600' : 'text-gray-500'}`}>
                    Serving:
                  </span>
                  <span className={isEnabled ? 'text-gray-900' : 'text-gray-600'}>
                    {campaign.servingStatus}
                  </span>
                </div>
              )}
              {campaign.adServingOptimizationStatus && (
                <div className="flex items-center gap-1.5">
                  <Target className="h-3.5 w-3.5 text-indigo-500" />
                  <span className={`font-medium ${isEnabled ? 'text-gray-600' : 'text-gray-500'}`}>
                    Optimization:
                  </span>
                  <span className={isEnabled ? 'text-gray-900' : 'text-gray-600'}>
                    {campaign.adServingOptimizationStatus}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {(campaign.adGroupIds?.length || campaign.assetGroupIds?.length) && (
          <div className="mt-4 border-t border-gray-200 pt-3">
            <div className="space-y-2 text-xs">
              {campaign.adGroupIds?.length > 0 && (
                <div className="flex items-center gap-1.5">
                  <Layers className="h-3.5 w-3.5 text-blue-500" />
                  <span className={`font-medium ${isEnabled ? 'text-gray-600' : 'text-gray-500'}`}>
                    Ad Groups:
                  </span>
                  <span
                    className={`font-semibold ${isEnabled ? 'text-gray-900' : 'text-gray-600'}`}
                  >
                    {campaign.adGroupIds.length}
                  </span>
                </div>
              )}
              {campaign.assetGroupIds?.length > 0 && (
                <div className="flex items-center gap-1.5">
                  <Layers className="h-3.5 w-3.5 text-indigo-500" />
                  <span className={`font-medium ${isEnabled ? 'text-gray-600' : 'text-gray-500'}`}>
                    Asset Groups:
                  </span>
                  <span
                    className={`font-semibold ${isEnabled ? 'text-gray-900' : 'text-gray-600'}`}
                  >
                    {campaign.assetGroupIds.length}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function AdGroupItem({ adGroup, className }: { adGroup: any; className?: string }) {
  const isEnabled = adGroup.status === 'ENABLED'

  return (
    <div
      className={`rounded-3xl border border-gray-200/50 text-black shadow-sm backdrop-blur-sm transition-shadow hover:shadow-md ${isEnabled ? 'bg-white/70' : 'bg-gray-50 opacity-60'} ${className}`}
    >
      <div className="px-5 py-4">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100">
              <Layers className="h-4 w-4 text-blue-600" />
            </div>
            <div
              className={`text-sm font-semibold ${isEnabled ? 'text-gray-900' : 'text-gray-500'}`}
            >
              {adGroup.adGroupName}
            </div>
          </div>
          <span
            className={`rounded-full px-2.5 py-1 text-xs font-medium ${getStatusColor(adGroup.status)}`}
          >
            {adGroup.status}
          </span>
        </div>

        {!isEnabled && (
          <div className="mb-3 text-xs italic text-gray-500">Ad group is not enabled</div>
        )}

        <div className="grid grid-cols-2 gap-4 text-xs">
          <div className="flex items-start gap-2.5">
            <Settings className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
            <div className="min-w-0">
              <div
                className={`truncate font-medium ${isEnabled ? 'text-gray-900' : 'text-gray-600'}`}
              >
                {adGroup.adGroupId}
              </div>
              <div className={isEnabled ? 'text-gray-500' : 'text-gray-400'}>Ad Group ID</div>
            </div>
          </div>

          <div className="flex items-start gap-2.5">
            <User className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
            <div className="min-w-0">
              <div
                className={`truncate font-medium ${isEnabled ? 'text-gray-900' : 'text-gray-600'}`}
              >
                {adGroup.customerId}
              </div>
              <div className={isEnabled ? 'text-gray-500' : 'text-gray-400'}>Customer ID</div>
            </div>
          </div>

          <div className="flex items-start gap-2.5">
            <TrendingUp className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
            <div className="min-w-0">
              <div
                className={`truncate font-medium ${isEnabled ? 'text-gray-900' : 'text-gray-600'}`}
              >
                {adGroup.campaignName}
              </div>
              <div className={isEnabled ? 'text-gray-500' : 'text-gray-400'}>Campaign</div>
            </div>
          </div>

          {adGroup.adGroupType && (
            <div className="flex items-start gap-2.5">
              <Target className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
              <div className="min-w-0">
                <div
                  className={`truncate font-medium ${isEnabled ? 'text-gray-900' : 'text-gray-600'}`}
                >
                  {adGroup.adGroupType}
                </div>
                <div className={isEnabled ? 'text-gray-500' : 'text-gray-400'}>Type</div>
              </div>
            </div>
          )}
        </div>

        {(adGroup.trackingUrlTemplate || adGroup.finalUrlSuffix) && (
          <div className="mt-4 border-t border-gray-200 pt-3">
            <div className="space-y-2 text-xs">
              {adGroup.trackingUrlTemplate && (
                <div className="flex items-start gap-1.5">
                  <Link className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-blue-500" />
                  <div className="min-w-0 flex-1">
                    <span
                      className={`font-medium ${isEnabled ? 'text-gray-600' : 'text-gray-500'}`}
                    >
                      Tracking Template:{' '}
                    </span>
                    <span className={`break-all ${isEnabled ? 'text-gray-900' : 'text-gray-600'}`}>
                      {adGroup.trackingUrlTemplate}
                    </span>
                  </div>
                </div>
              )}
              {adGroup.finalUrlSuffix && (
                <div className="flex items-start gap-1.5">
                  <Link className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-green-500" />
                  <div className="min-w-0 flex-1">
                    <span
                      className={`font-medium ${isEnabled ? 'text-gray-600' : 'text-gray-500'}`}
                    >
                      URL Suffix:{' '}
                    </span>
                    <span className={`break-all ${isEnabled ? 'text-gray-900' : 'text-gray-600'}`}>
                      {adGroup.finalUrlSuffix}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {adGroup.adIds?.length > 0 && (
          <div className="mt-4 border-t border-gray-200 pt-3">
            <div className="flex items-center gap-1.5 text-xs">
              <MousePointerClick className="h-3.5 w-3.5 text-purple-500" />
              <span className={`font-medium ${isEnabled ? 'text-gray-600' : 'text-gray-500'}`}>
                Ads:
              </span>
              <span className={`font-semibold ${isEnabled ? 'text-gray-900' : 'text-gray-600'}`}>
                {adGroup.adIds.length}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function AssetGroupItem({ assetGroup, className }: { assetGroup: any; className?: string }) {
  const formattedAdStrength = formatAdStrength(assetGroup.adStrength)
  const isEnabled = assetGroup.status === 'ENABLED'

  return (
    <div
      className={`rounded-3xl border border-gray-200/50 text-black shadow-sm backdrop-blur-sm transition-shadow hover:shadow-md ${isEnabled ? 'bg-white/70' : 'bg-gray-50 opacity-60'} ${className}`}
    >
      <div className="px-5 py-4">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-100">
              <Layers className="h-4 w-4 text-indigo-600" />
            </div>
            <div
              className={`text-sm font-semibold ${isEnabled ? 'text-gray-900' : 'text-gray-500'}`}
            >
              {assetGroup.assetGroupName}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {formattedAdStrength && (
              <span
                className={`rounded-full px-2.5 py-1 text-xs font-medium ${getAdStrengthColor(assetGroup.adStrength)}`}
                title={`Ad Strength: ${formattedAdStrength}`}
              >
                {formattedAdStrength}
              </span>
            )}
            <span
              className={`rounded-full px-2.5 py-1 text-xs font-medium ${getStatusColor(assetGroup.status)}`}
            >
              {assetGroup.status}
            </span>
          </div>
        </div>

        {!isEnabled && (
          <div className="mb-3 text-xs italic text-gray-500">Asset group is not enabled</div>
        )}

        <div className="grid grid-cols-2 gap-4 text-xs">
          <div className="flex items-start gap-2.5">
            <Settings className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
            <div className="min-w-0">
              <div
                className={`truncate font-medium ${isEnabled ? 'text-gray-900' : 'text-gray-600'}`}
              >
                {assetGroup.assetGroupId}
              </div>
              <div className={isEnabled ? 'text-gray-500' : 'text-gray-400'}>Asset Group ID</div>
            </div>
          </div>

          <div className="flex items-start gap-2.5">
            <User className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
            <div className="min-w-0">
              <div
                className={`truncate font-medium ${isEnabled ? 'text-gray-900' : 'text-gray-600'}`}
              >
                {assetGroup.customerId}
              </div>
              <div className={isEnabled ? 'text-gray-500' : 'text-gray-400'}>Customer ID</div>
            </div>
          </div>

          <div className="flex items-start gap-2.5">
            <TrendingUp className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
            <div className="min-w-0">
              <div
                className={`truncate font-medium ${isEnabled ? 'text-gray-900' : 'text-gray-600'}`}
              >
                {assetGroup.campaignName}
              </div>
              <div className={isEnabled ? 'text-gray-500' : 'text-gray-400'}>Campaign</div>
            </div>
          </div>

          {assetGroup.path1 && (
            <div className="flex items-start gap-2.5">
              <Link className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
              <div className="min-w-0">
                <div
                  className={`truncate font-medium ${isEnabled ? 'text-gray-900' : 'text-gray-600'}`}
                >
                  {assetGroup.path1}
                </div>
                <div className={isEnabled ? 'text-gray-500' : 'text-gray-400'}>Path 1</div>
              </div>
            </div>
          )}

          {assetGroup.path2 && (
            <div className="flex items-start gap-2.5">
              <Link className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
              <div className="min-w-0">
                <div
                  className={`truncate font-medium ${isEnabled ? 'text-gray-900' : 'text-gray-600'}`}
                >
                  {assetGroup.path2}
                </div>
                <div className={isEnabled ? 'text-gray-500' : 'text-gray-400'}>Path 2</div>
              </div>
            </div>
          )}
        </div>

        {assetGroup.finalUrls?.length > 0 && (
          <div className="mt-4 border-t border-gray-200 pt-3">
            <div className="space-y-2 text-xs">
              <div className="flex items-center gap-1.5">
                <Link className="h-3.5 w-3.5 text-blue-500" />
                <span className={`font-medium ${isEnabled ? 'text-gray-600' : 'text-gray-500'}`}>
                  Final URLs
                </span>
              </div>
              <div className="ml-5 space-y-1">
                {assetGroup.finalUrls.map((url: string, index: number) => (
                  <a
                    key={index}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block truncate text-blue-600 hover:text-blue-800 hover:underline"
                    title={url}
                  >
                    {url}
                  </a>
                ))}
              </div>
            </div>
          </div>
        )}

        {assetGroup.finalMobileUrls?.length > 0 && (
          <div className="mt-4 border-t border-gray-200 pt-3">
            <div className="space-y-2 text-xs">
              <div className="flex items-center gap-1.5">
                <Link className="h-3.5 w-3.5 text-purple-500" />
                <span className={`font-medium ${isEnabled ? 'text-gray-600' : 'text-gray-500'}`}>
                  Mobile URLs
                </span>
              </div>
              <div className="ml-5 space-y-1">
                {assetGroup.finalMobileUrls.map((url: string, index: number) => (
                  <a
                    key={index}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block truncate text-purple-600 hover:text-purple-800 hover:underline"
                    title={url}
                  >
                    {url}
                  </a>
                ))}
              </div>
            </div>
          </div>
        )}

        {assetGroup.finalUrlSuffix && (
          <div className="mt-4 border-t border-gray-200 pt-3">
            <div className="flex items-start gap-1.5 text-xs">
              <Link className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-green-500" />
              <div className="min-w-0 flex-1">
                <span className={`font-medium ${isEnabled ? 'text-gray-600' : 'text-gray-500'}`}>
                  URL Suffix:{' '}
                </span>
                <span className={`break-all ${isEnabled ? 'text-gray-900' : 'text-gray-600'}`}>
                  {assetGroup.finalUrlSuffix}
                </span>
              </div>
            </div>
          </div>
        )}

        {assetGroup.adIds?.length > 0 && (
          <div className="mt-4 border-t border-gray-200 pt-3">
            <div className="flex items-center gap-1.5 text-xs">
              <MousePointerClick className="h-3.5 w-3.5 text-purple-500" />
              <span className={`font-medium ${isEnabled ? 'text-gray-600' : 'text-gray-500'}`}>
                Ads:
              </span>
              <span className={`font-semibold ${isEnabled ? 'text-gray-900' : 'text-gray-600'}`}>
                {assetGroup.adIds.length}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function AdItem({ ad, className }: { ad: any; className?: string }) {
  const isEnabled = ad.status === 'ENABLED'

  return (
    <div
      className={`rounded-3xl border border-gray-200/50 text-black shadow-sm ${isEnabled ? 'bg-white/70' : 'bg-gray-50 opacity-60'} ${className}`}
    >
      <div className="px-4 py-3">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MousePointerClick className="h-4 w-4 text-purple-500" />
            <div
              className={`text-sm font-semibold ${isEnabled ? 'text-gray-900' : 'text-gray-500'}`}
            >
              {ad.adName || ad.adType}
              {ad.addedByGoogleAds && (
                <span
                  className={`ml-2 text-xs font-normal ${isEnabled ? 'text-gray-500' : 'text-gray-400'}`}
                >
                  (Auto-generated)
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span
              className={`rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(ad.status)}`}
            >
              {ad.status}
            </span>
          </div>
        </div>

        {!isEnabled && <div className="mb-3 text-xs italic text-gray-500">Ad is not enabled</div>}

        <div className="grid grid-cols-2 gap-3 text-xs">
          <div className="flex items-center gap-2">
            <Settings className="h-3 w-3 text-gray-500" />
            <div>
              <div className={`font-medium ${isEnabled ? 'text-gray-900' : 'text-gray-600'}`}>
                {ad.adId}
              </div>
              <div className={isEnabled ? 'text-gray-600' : 'text-gray-500'}>Ad ID</div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <User className="h-3 w-3 text-gray-500" />
            <div>
              <div className={`font-medium ${isEnabled ? 'text-gray-900' : 'text-gray-600'}`}>
                {ad.customerId}
              </div>
              <div className={isEnabled ? 'text-gray-600' : 'text-gray-500'}>Customer ID</div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Target className="h-3 w-3 text-gray-500" />
            <div>
              <div className={`font-medium ${isEnabled ? 'text-gray-900' : 'text-gray-600'}`}>
                {ad.adType}
              </div>
              <div className={isEnabled ? 'text-gray-600' : 'text-gray-500'}>Ad Type</div>
            </div>
          </div>

          {ad.campaignName && (
            <div className="flex items-center gap-2">
              <TrendingUp className="h-3 w-3 text-gray-500" />
              <div>
                <div className={`font-medium ${isEnabled ? 'text-gray-900' : 'text-gray-600'}`}>
                  {ad.campaignName}
                </div>
                <div className={isEnabled ? 'text-gray-600' : 'text-gray-500'}>Campaign</div>
              </div>
            </div>
          )}

          {ad.adGroupName && (
            <div className="flex items-center gap-2">
              <Layers className="h-3 w-3 text-gray-500" />
              <div>
                <div className={`font-medium ${isEnabled ? 'text-gray-900' : 'text-gray-600'}`}>
                  {ad.adGroupName}
                </div>
                <div className={isEnabled ? 'text-gray-600' : 'text-gray-500'}>Ad Group</div>
              </div>
            </div>
          )}

          {ad.assetGroupName && (
            <div className="flex items-center gap-2">
              <Layers className="h-3 w-3 text-gray-500" />
              <div>
                <div className={`font-medium ${isEnabled ? 'text-gray-900' : 'text-gray-600'}`}>
                  {ad.assetGroupName}
                </div>
                <div className={isEnabled ? 'text-gray-600' : 'text-gray-500'}>Asset Group</div>
              </div>
            </div>
          )}

          {ad.displayUrl && (
            <div className="flex items-center gap-2">
              <Link className="h-3 w-3 text-gray-500" />
              <div>
                <div className={`font-medium ${isEnabled ? 'text-gray-900' : 'text-gray-600'}`}>
                  {ad.displayUrl}
                </div>
                <div className={isEnabled ? 'text-gray-600' : 'text-gray-500'}>Display URL</div>
              </div>
            </div>
          )}
        </div>

        {ad.finalUrls?.length > 0 && (
          <div className="mt-3 border-t border-gray-200 pt-2">
            <div className="space-y-1 text-xs">
              <div className="flex items-center gap-1">
                <Link className="h-3 w-3 text-blue-500" />
                <span className={isEnabled ? 'text-gray-600' : 'text-gray-500'}>Final URLs:</span>
              </div>
              {ad.finalUrls.map((url: string, index: number) => (
                <div
                  key={index}
                  className={`ml-4 truncate ${isEnabled ? 'text-gray-900' : 'text-gray-600'}`}
                >
                  {url}
                </div>
              ))}
            </div>
          </div>
        )}

        {(ad.trackingUrlTemplate || ad.finalUrlSuffix) && (
          <div className="mt-3 border-t border-gray-200 pt-2">
            <div className="space-y-1 text-xs">
              {ad.trackingUrlTemplate && (
                <div className="flex items-start gap-1">
                  <Link className="mt-0.5 h-3 w-3 flex-shrink-0 text-blue-500" />
                  <div className="min-w-0 flex-1">
                    <span className={isEnabled ? 'text-gray-600' : 'text-gray-500'}>
                      Tracking Template:{' '}
                    </span>
                    <span className={`break-all ${isEnabled ? 'text-gray-900' : 'text-gray-600'}`}>
                      {ad.trackingUrlTemplate}
                    </span>
                  </div>
                </div>
              )}
              {ad.finalUrlSuffix && (
                <div className="flex items-start gap-1">
                  <Link className="mt-0.5 h-3 w-3 flex-shrink-0 text-green-500" />
                  <div className="min-w-0 flex-1">
                    <span className={isEnabled ? 'text-gray-600' : 'text-gray-500'}>
                      URL Suffix:{' '}
                    </span>
                    <span className={`break-all ${isEnabled ? 'text-gray-900' : 'text-gray-600'}`}>
                      {ad.finalUrlSuffix}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function GoogleAdsListCampaignsDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(
    googleAdsListCampaigns,
    nodeExecution.inputs?.functionArgs || nodeExecution.inputs
  )
  const [showJson, setShowJson] = useState(false)
  const [showDisabled, setShowDisabled] = useState(false)

  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data as ResourceResult
  const resources: ResourceItem[] = Array.isArray(result) ? result : [result]

  const actionName = nodeExecution.inputs?.functionArgs.args.action?.name

  // Sort resources: enabled first, then paused, then others
  const sortedResources = [...resources].sort((a, b) => {
    const aIsEnabled = a.status === 'ENABLED'
    const bIsEnabled = b.status === 'ENABLED'

    // Enabled items go to the top
    if (aIsEnabled && !bIsEnabled) return -1
    if (!aIsEnabled && bIsEnabled) return 1
    if (aIsEnabled && bIsEnabled) return 0

    // Among non-enabled, paused comes before others
    const aIsPaused = a.status === 'PAUSED'
    const bIsPaused = b.status === 'PAUSED'
    if (aIsPaused && !bIsPaused) return -1
    if (!aIsPaused && bIsPaused) return 1

    return 0
  })

  // Separate enabled and non-enabled resources
  const enabledResources = sortedResources.filter((r) => r.status === 'ENABLED')
  const disabledResources = sortedResources.filter((r) => r.status !== 'ENABLED')

  // Determine which resources to show
  const visibleResources = showDisabled ? sortedResources : enabledResources

  const getResourceInfo = () => {
    switch (actionName) {
      case 'listCampaigns':
        return {
          title: 'Google Ads Campaigns',
          icon: <TrendingUp className="h-5 w-5 text-gray-600" />,
          emptyMessage: 'No campaigns found',
          itemRenderer: (item: any, index: number) => (
            <CampaignItem key={item.campaignId || index} campaign={item} className="mx-8" />
          ),
        }
      case 'listGroups':
        return {
          title: 'Google Ads Groups',
          icon: <Layers className="h-5 w-5 text-teal-600" />,
          emptyMessage: 'No groups found',
          itemRenderer: (item: any, index: number) => {
            if (item.adGroupId) {
              return <AdGroupItem key={item.adGroupId || index} adGroup={item} className="mx-8" />
            } else if (item.assetGroupId) {
              return (
                <AssetGroupItem
                  key={item.assetGroupId || index}
                  assetGroup={item}
                  className="mx-8"
                />
              )
            }
            return (
              <div key={index} className="text-gray-500">
                Unknown group type
              </div>
            )
          },
        }
      case 'listAds':
        return {
          title: 'Google Ads Ads',
          icon: <MousePointerClick className="h-5 w-5 text-purple-600" />,
          emptyMessage: 'No ads found',
          itemRenderer: (item: any, index: number) => (
            <AdItem key={item.adId || index} ad={item} className="mx-8" />
          ),
        }
      default:
        return {
          title: 'Google Ads Resources',
          icon: <TrendingUp className="h-5 w-5 text-gray-600" />,
          emptyMessage: 'No resources found',
          itemRenderer: (item: any, index: number) => (
            <CampaignItem key={index} campaign={item} className="mx-8" />
          ),
        }
    }
  }

  const resourceInfo = getResourceInfo()
  const enabledCount = resources.filter((c) => c.status === 'ENABLED').length
  const pausedCount = resources.filter((c) => c.status === 'PAUSED').length
  const removedCount = resources.filter((c) => c.status === 'REMOVED').length

  // Calculate additional stats based on action type
  const getAdditionalStats = () => {
    if (actionName === 'listGroups') {
      const adGroups = resources.filter((r: any) => r.adGroupId)
      const assetGroups = resources.filter((r: any) => r.assetGroupId)
      return {
        adGroups: adGroups.length,
        assetGroups: assetGroups.length,
      }
    }
    if (actionName === 'listCampaigns') {
      const campaignTypes = resources.reduce((acc: Record<string, number>, r: any) => {
        const type = r.campaignType || 'UNKNOWN'
        acc[type] = (acc[type] || 0) + 1
        return acc
      }, {})
      return { campaignTypes }
    }
    return {}
  }

  const additionalStats = getAdditionalStats()

  if (showJson) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">JSON View</h3>
          <button
            onClick={() => setShowJson(false)}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            Show Formatted View
          </button>
        </div>
        <ObjectDisplay data={nodeExecution.outputs} />
      </div>
    )
  }

  if (resources.length === 0) {
    return (
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="flex-shrink-0">{resourceInfo.icon}</div>
          <div className="text-sm text-gray-600">{resourceInfo.emptyMessage}</div>
        </div>
      </div>
    )
  }

  if (resources.length === 1) {
    const item = resources[0] as any
    switch (actionName) {
      case 'listCampaigns':
        return <CampaignItem campaign={item} />
      case 'listGroups':
        if (item.adGroupId) {
          return <AdGroupItem adGroup={item} />
        } else if (item.assetGroupId) {
          return <AssetGroupItem assetGroup={item} />
        }
        return <div className="text-gray-500">Unknown group type</div>
      case 'listAds':
        return <AdItem ad={item} />
      default:
        return <CampaignItem campaign={item} />
    }
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">{resourceInfo.title}</h3>
          <p className="text-xs text-gray-600">
            {enabledResources.length} enabled
            {disabledResources.length > 0 && (
              <span className="text-gray-500"> • {disabledResources.length} disabled</span>
            )}
          </p>
        </div>
        <button
          onClick={() => setShowJson(true)}
          className="text-xs text-blue-600 hover:text-blue-800"
        >
          View JSON
        </button>
      </div>

      {/* Summary Stats Blocks */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <div className="mb-1 flex items-center gap-1">
            {resourceInfo.icon}
            <span className="text-xs font-medium text-gray-700">Total</span>
          </div>
          <div className="text-lg font-bold text-gray-900">{resources.length}</div>
        </div>

        {enabledCount > 0 && (
          <div className="rounded-lg border border-gray-200 bg-green-50 p-3">
            <div className="mb-1 flex items-center gap-1">
              <div className="h-3 w-3 rounded-full bg-green-500" />
              <span className="text-xs font-medium text-gray-700">Enabled</span>
            </div>
            <div className="text-lg font-bold text-gray-900">{enabledCount}</div>
          </div>
        )}

        {pausedCount > 0 && (
          <div className="rounded-lg border border-gray-200 bg-yellow-50 p-3">
            <div className="mb-1 flex items-center gap-1">
              <div className="h-3 w-3 rounded-full bg-yellow-500" />
              <span className="text-xs font-medium text-gray-700">Paused</span>
            </div>
            <div className="text-lg font-bold text-gray-900">{pausedCount}</div>
          </div>
        )}

        {removedCount > 0 && (
          <div className="rounded-lg border border-gray-200 bg-red-50 p-3">
            <div className="mb-1 flex items-center gap-1">
              <div className="h-3 w-3 rounded-full bg-red-500" />
              <span className="text-xs font-medium text-gray-700">Removed</span>
            </div>
            <div className="text-lg font-bold text-gray-900">{removedCount}</div>
          </div>
        )}

        {actionName === 'listGroups' &&
          'adGroups' in additionalStats &&
          'assetGroups' in additionalStats && (
            <>
              {(additionalStats as { adGroups: number; assetGroups: number }).adGroups > 0 && (
                <div className="rounded-lg border border-gray-200 bg-blue-50 p-3">
                  <div className="mb-1 flex items-center gap-1">
                    <Layers className="h-3 w-3 text-blue-600" />
                    <span className="text-xs font-medium text-gray-700">Ad Groups</span>
                  </div>
                  <div className="text-lg font-bold text-gray-900">
                    {(additionalStats as { adGroups: number; assetGroups: number }).adGroups}
                  </div>
                </div>
              )}
              {(additionalStats as { adGroups: number; assetGroups: number }).assetGroups > 0 && (
                <div className="rounded-lg border border-gray-200 bg-indigo-50 p-3">
                  <div className="mb-1 flex items-center gap-1">
                    <Layers className="h-3 w-3 text-indigo-600" />
                    <span className="text-xs font-medium text-gray-700">Asset Groups</span>
                  </div>
                  <div className="text-lg font-bold text-gray-900">
                    {(additionalStats as { adGroups: number; assetGroups: number }).assetGroups}
                  </div>
                </div>
              )}
            </>
          )}
      </div>

      {/* All IDs Summary */}
      <div className="rounded-lg border border-gray-200 bg-white p-3">
        <details className="group">
          <summary className="cursor-pointer text-xs font-medium text-gray-700 hover:text-gray-900">
            View All IDs ({sortedResources.length})
          </summary>
          <div className="mt-2 space-y-1 text-xs">
            {sortedResources.map((item: any, index: number) => {
              const id =
                item.campaignId || item.adGroupId || item.assetGroupId || item.adId || `#${index}`
              const name =
                item.campaignName ||
                item.adGroupName ||
                item.assetGroupName ||
                item.adName ||
                'Unnamed'
              return (
                <div key={index} className="flex items-center justify-between gap-2 py-1">
                  <span className="truncate font-medium text-gray-900">{name}</span>
                  <span className="flex-shrink-0 font-mono text-gray-600">{id}</span>
                </div>
              )
            })}
          </div>
        </details>
      </div>

      {/* Resources List */}
      <div className="space-y-2">
        {visibleResources.map((item: ResourceItem, index: number) =>
          resourceInfo.itemRenderer(item, index)
        )}

        {/* Show/Hide Disabled Button */}
        {disabledResources.length > 0 && !showDisabled && (
          <button
            onClick={() => setShowDisabled(true)}
            className="mt-3 w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100"
          >
            Show {disabledResources.length} disabled{' '}
            {actionName === 'listCampaigns'
              ? 'campaign'
              : actionName === 'listGroups'
                ? 'group'
                : actionName === 'listAds'
                  ? 'ad'
                  : 'resource'}
            {disabledResources.length !== 1 ? 's' : ''}
          </button>
        )}

        {disabledResources.length > 0 && showDisabled && (
          <button
            onClick={() => setShowDisabled(false)}
            className="mt-3 w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100"
          >
            Hide {disabledResources.length} disabled{' '}
            {actionName === 'listCampaigns'
              ? 'campaign'
              : actionName === 'listGroups'
                ? 'group'
                : actionName === 'listAds'
                  ? 'ad'
                  : 'resource'}
            {disabledResources.length !== 1 ? 's' : ''}
          </button>
        )}
      </div>
    </div>
  )
}

function downloadGoogleAdsListCampaigns(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function googleAdsListCampaignsToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(
    googleAdsListCampaigns,
    nodeExecution.inputs?.functionArgs || nodeExecution.inputs
  )
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data as ResourceResult
  const resources: ResourceItem[] = Array.isArray(result) ? result : [result]
  const actionName = nodeExecution.inputs?.functionArgs.args.action?.name

  if (resources.length === 0) {
    return `## Google Ads Resources\n\nNo resources found.\n\n---`
  }

  const toMarkdown = () => {
    switch (actionName) {
      case 'listCampaigns':
        return resources
          .map((campaign: any) => {
            let info = `### ${campaign.campaignName} (${campaign.campaignType} - ${campaign.status})\n\n`
            info += `**Campaign ID:** ${campaign.campaignId}\n`
            info += `**Customer:** ${campaign.customerName || campaign.customerId}\n`
            info += `**Type:** ${campaign.campaignType}\n`
            info += `**Status:** ${campaign.status}\n`
            if (campaign.biddingStrategy)
              info += `**Bidding Strategy:** ${campaign.biddingStrategy}\n`
            if (campaign.budget?.amount)
              info += `**Budget:** $${campaign.budget.amount.toFixed(2)}\n`
            if (campaign.startDate) info += `**Start Date:** ${campaign.startDate}\n`
            if (campaign.endDate) info += `**End Date:** ${campaign.endDate}\n`
            if (campaign.servingStatus) info += `**Serving Status:** ${campaign.servingStatus}\n`
            if (campaign.adServingOptimizationStatus)
              info += `**Optimization Status:** ${campaign.adServingOptimizationStatus}\n`
            if (campaign.adGroupIds?.length)
              info += `**Ad Groups:** ${campaign.adGroupIds.length}\n`
            if (campaign.assetGroupIds?.length)
              info += `**Asset Groups:** ${campaign.assetGroupIds.length}\n`
            return info
          })
          .join('\n---\n\n')

      case 'listGroups':
        return resources
          .map((group: any) => {
            if (group.adGroupId) {
              let info = `### ${group.adGroupName} (${group.status})\n\n`
              info += `**Ad Group ID:** ${group.adGroupId}\n`
              info += `**Customer ID:** ${group.customerId}\n`
              info += `**Campaign:** ${group.campaignName}\n`
              info += `**Status:** ${group.status}\n`
              if (group.adGroupType) info += `**Type:** ${group.adGroupType}\n`
              if (group.trackingUrlTemplate)
                info += `**Tracking Template:** ${group.trackingUrlTemplate}\n`
              if (group.finalUrlSuffix) info += `**URL Suffix:** ${group.finalUrlSuffix}\n`
              if (group.adIds?.length) info += `**Ads:** ${group.adIds.length}\n`
              return info
            } else if (group.assetGroupId) {
              let info = `### ${group.assetGroupName} (${group.status})\n\n`
              info += `**Asset Group ID:** ${group.assetGroupId}\n`
              info += `**Customer ID:** ${group.customerId}\n`
              info += `**Campaign:** ${group.campaignName}\n`
              info += `**Status:** ${group.status}\n`
              if (group.adStrength) info += `**Ad Strength:** ${group.adStrength}\n`
              if (group.path1) info += `**Path 1:** ${group.path1}\n`
              if (group.path2) info += `**Path 2:** ${group.path2}\n`
              if (group.finalUrls?.length) {
                info += `**Final URLs:**\n`
                group.finalUrls.forEach((url: string) => {
                  info += `  - ${url}\n`
                })
              }
              if (group.finalMobileUrls?.length) {
                info += `**Mobile URLs:**\n`
                group.finalMobileUrls.forEach((url: string) => {
                  info += `  - ${url}\n`
                })
              }
              if (group.finalUrlSuffix) info += `**URL Suffix:** ${group.finalUrlSuffix}\n`
              if (group.adIds?.length) info += `**Ads:** ${group.adIds.length}\n`
              return info
            }
            return '### Unknown group type\n\n'
          })
          .join('\n---\n\n')

      case 'listAds':
        return resources
          .map((ad: any) => {
            let info = `### ${ad.adName || ad.adType} (${ad.status})\n\n`
            info += `**Ad ID:** ${ad.adId}\n`
            if (ad.adName) info += `**Ad Name:** ${ad.adName}\n`
            info += `**Ad Type:** ${ad.adType}\n`
            info += `**Customer ID:** ${ad.customerId}\n`
            if (ad.campaignName) info += `**Campaign:** ${ad.campaignName}\n`
            if (ad.adGroupName) info += `**Ad Group:** ${ad.adGroupName}\n`
            if (ad.assetGroupName) info += `**Asset Group:** ${ad.assetGroupName}\n`
            info += `**Status:** ${ad.status}\n`
            if (ad.addedByGoogleAds) info += `**Auto-generated:** Yes\n`
            if (ad.displayUrl) info += `**Display URL:** ${ad.displayUrl}\n`
            if (ad.finalUrls?.length) {
              info += `**Final URLs:**\n`
              ad.finalUrls.forEach((url: string) => {
                info += `  - ${url}\n`
              })
            }
            if (ad.trackingUrlTemplate) info += `**Tracking Template:** ${ad.trackingUrlTemplate}\n`
            if (ad.finalUrlSuffix) info += `**URL Suffix:** ${ad.finalUrlSuffix}\n`
            return info
          })
          .join('\n---\n\n')

      default:
        return 'Unknown resource type'
    }
  }

  const getTitle = () => {
    switch (actionName) {
      case 'listCampaigns':
        return `Google Ads Campaigns (${resources.length})`
      case 'listGroups':
        return `Google Ads Groups (${resources.length})`
      case 'listAds':
        return `Google Ads Ads (${resources.length})`
      default:
        return `Google Ads Resources (${resources.length})`
    }
  }

  return `## ${getTitle()}\n\n${toMarkdown()}\n\n---`
}

export const GoogleAdsListCampaignsDisplay = createWorkflowToolComponent(
  GoogleAdsListCampaignsDisplayComponent,
  downloadGoogleAdsListCampaigns,
  googleAdsListCampaignsToMarkdown
)
